/**
 * MANIPULACIÓN MÍNIMA DE URLSearchParams PARA LISTADOS — FASE 3.3-B
 *
 * Esto NO es un framework de filtros. Sólo sabe leer un parámetro con su
 * valor por defecto, fijarlo (quitándolo de la URL si vuelve a ser el
 * default, para que una URL "limpia" sea indistinguible de "sin filtrar"),
 * y limpiar. No sabe qué significa `estado`, `orden` ni ningún otro nombre
 * de parámetro — esa semántica es de cada página, que decide qué claves
 * gestiona y qué hace con cada valor.
 *
 * `paginaClave`, si se indica, es la clave que se resetea a su default cada
 * vez que cambia CUALQUIER OTRA clave gestionada — el "cambiar un filtro
 * vuelve a la página 1" que pide cualquier listado paginado. Un listado sin
 * paginación (como Clientes/Centros en 3.3-B1) simplemente no la declara.
 */
import { useLocation, useSearchParams } from 'react-router-dom';

export interface ListaUrl<K extends string> {
  /** Valor actual del parámetro, o su default si no está en la URL. */
  valor(clave: K): string;
  /** Fija un parámetro; si coincide con su default, lo quita de la URL. */
  fijar(clave: K, valor: string): void;
  /** Fija varios parámetros a la vez, en una sola actualización de la URL. */
  fijarVarios(cambios: Partial<Record<K, string>>): void;
  /** Quita de la URL todas las claves gestionadas, volviendo a los defaults. */
  limpiar(): void;
  /** true si todas las claves gestionadas están en su valor por defecto. */
  esPorDefecto: boolean;
}

export function useListaUrl<K extends string>(
  defaults: Record<K, string>,
  //  `string`, no `K`: si fuera `K`, pasar un literal aquí hace que TS
  //  infiera `K` sólo a partir de este argumento (en vez de `defaults`) y
  //  estrecha el genérico a esa única clave — un problema conocido de
  //  inferencia con `Record<K, string>` en sentido inverso.
  paginaClave?: string,
): ListaUrl<K> {
  const [params, setParams] = useSearchParams();
  //  F10.4: cambiar un filtro no debe perder el estado de navegación con el que se llegó (p. ej. el
  //  «volver» del Planner al plan): `setSearchParams` lo descarta si no se le pasa.
  const estadoNavegacion: unknown = useLocation().state;
  const claves = Object.keys(defaults) as K[];

  function valor(clave: K): string {
    return params.get(clave) ?? defaults[clave];
  }

  function fijarVarios(cambios: Partial<Record<K, string>>): void {
    const p = new URLSearchParams(params);
    for (const clave of Object.keys(cambios) as K[]) {
      const val = cambios[clave];
      if (val === undefined) continue;
      if (val === defaults[clave]) p.delete(clave);
      else p.set(clave, val);
    }
    if (paginaClave !== undefined && !(paginaClave in cambios)) {
      p.delete(paginaClave);
    }
    setParams(p, { replace: true, state: estadoNavegacion });
  }

  function fijar(clave: K, val: string): void {
    fijarVarios({ [clave]: val } as Partial<Record<K, string>>);
  }

  function limpiar(): void {
    const p = new URLSearchParams(params);
    for (const clave of claves) p.delete(clave);
    setParams(p, { replace: true, state: estadoNavegacion });
  }

  const esPorDefecto = claves.every((clave) => valor(clave) === defaults[clave]);

  return { valor, fijar, fijarVarios, limpiar, esPorDefecto };
}
